import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface TimezoneComboboxProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  /** Combobox trigger width. */
  className?: string;
}

const FALLBACK_ZONES = [
  'UTC',
  'America/Los_Angeles',
  'America/New_York',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Yerevan',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
];

function allZones(): string[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const intl: any = Intl as any;
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      const zones = intl.supportedValuesOf('timeZone') as string[];
      const set = new Set(zones);
      set.add('UTC');
      return Array.from(set).sort();
    } catch {
      // fall through
    }
  }
  return FALLBACK_ZONES;
}

/**
 * GMT offset for a zone right now, e.g. "GMT+4" / "GMT-8". Computed via
 * Intl.DateTimeFormat with shortOffset; returns "" if the runtime can't
 * resolve the zone.
 */
function offsetOf(zone: string, now: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);
    const part = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (part) return part === 'GMT' ? 'GMT+0' : part;
  } catch {
    /* zone unsupported */
  }
  return '';
}

interface ZoneEntry {
  zone: string;     // 'Asia/Yerevan'
  region: string;   // 'Asia' (group label); 'Other' for top-level / unknown
  city: string;     // 'Yerevan'
  offset: string;   // 'GMT+4'
  label: string;    // '(GMT+4) Yerevan'
}

// Region order matches Google Calendar / Outlook conventions. Anything not
// recognized goes to "Other" at the bottom.
const REGION_ORDER = [
  'Africa',
  'America',
  'Antarctica',
  'Arctic',
  'Asia',
  'Atlantic',
  'Australia',
  'Europe',
  'Indian',
  'Pacific',
  'Etc',
  'Other',
];

function entryOf(zone: string, now: Date): ZoneEntry {
  const slash = zone.indexOf('/');
  const rawRegion = slash === -1 ? '' : zone.slice(0, slash);
  const tail = slash === -1 ? zone : zone.slice(slash + 1);
  const city = tail.replace(/_/g, ' ').replace(/\//g, ' / ');
  const offset = offsetOf(zone, now);
  const label = offset ? `(${offset}) ${city}` : city;
  const region = REGION_ORDER.includes(rawRegion) ? rawRegion : 'Other';
  return { zone, region, city, offset, label };
}

export function TimezoneCombobox({
  value,
  onChange,
  id,
  placeholder = 'Search timezones…',
  className,
}: TimezoneComboboxProps) {
  const [open, setOpen] = useState(false);

  const entries = useMemo<ZoneEntry[]>(() => {
    const now = new Date();
    return allZones()
      .map((z) => entryOf(z, now))
      .sort((a, b) => {
        const ga = REGION_ORDER.indexOf(a.region);
        const gb = REGION_ORDER.indexOf(b.region);
        if (ga !== gb) return ga - gb;
        return a.city.localeCompare(b.city);
      });
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, ZoneEntry[]>();
    for (const e of entries) {
      const arr = map.get(e.region) ?? [];
      arr.push(e);
      map.set(e.region, arr);
    }
    return REGION_ORDER.filter((r) => map.has(r)).map((r) => ({
      region: r,
      items: map.get(r)!,
    }));
  }, [entries]);

  const selected = entries.find((e) => e.zone === value);
  const triggerLabel = selected
    ? selected.label
    : value || 'Select a timezone';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal sm:max-w-md',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(itemValue, search) => {
            // cmdk passes value lowercased; match against the full label +
            // IANA id so users can search by city, offset, or zone id.
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            return itemValue.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            {groups.map((group, gi) => (
              <div key={group.region}>
                <CommandGroup heading={group.region}>
                  {group.items.map((e) => (
                    <CommandItem
                      key={e.zone}
                      // cmdk uses `value` for filtering — pack the search
                      // tokens in here, but commit the IANA zone via onSelect.
                      value={`${e.zone} ${e.city} ${e.offset}`.toLowerCase()}
                      onSelect={() => {
                        onChange(e.zone);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 size-4',
                          e.zone === value ? 'opacity-100' : 'opacity-0',
                        )}
                        aria-hidden
                      />
                      <span className="flex-1 truncate">{e.label}</span>
                      <span className="ml-2 shrink-0 text-xs font-mono text-muted-foreground">
                        {e.zone}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                {gi < groups.length - 1 && <CommandSeparator />}
              </div>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
